/**
 * GenericHealthMetrics Component
 *
 * Reusable collapsible panel showing blockchain health metrics:
 * - Network stats
 * - Development activity (GitHub commits, stars)
 * - Overall health indicator (growing/stable/declining)
 *
 * Uses cached data from server (24h TTL) to reduce API calls.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import {
  fetchNetworkHealth,
  refreshNetworkHealth,
  transformToGitHubStats,
  formatSupply,
  getTimeSinceUpdate,
  type BlockchainHealthData,
  type NetworkId,
  type GitHubStats,
  type TokenEmission,
} from '../../services/blockchainHealthApi';

type HealthStatus = 'growing' | 'stable' | 'declining' | 'loading';

export interface NetworkConfig {
  networkId: NetworkId;
  name: string;
  githubUrl: string;
  githubRepoName: string;
  gradientColors: string;
  primaryColor: string;
  renderNetworkMetrics?: (metrics: Record<string, any>) => React.ReactNode;
}

interface GenericHealthMetricsProps {
  config: NetworkConfig;
}

export function GenericHealthMetrics({ config }: GenericHealthMetricsProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cachedData, setCachedData] = useState<BlockchainHealthData | null>(null);
  const [healthStatus, setHealthStatus] = useState<HealthStatus>('loading');

  // Derived data from cache
  const githubStats: GitHubStats | null = cachedData ? transformToGitHubStats(cachedData) : null;
  const networkMetrics: Record<string, any> | null = cachedData?.network_metrics || null;
  const nextEmissions: TokenEmission[] = cachedData?.token_emissions || [];

  // Load data from cache API
  const loadData = async (forceRefresh = false) => {
    setIsLoading(true);

    try {
      const data = forceRefresh
        ? await refreshNetworkHealth(config.networkId)
        : await fetchNetworkHealth(config.networkId);

      if (data) {
        setCachedData(data);
        setHealthStatus(data.health_status as HealthStatus);
      }
    } catch (error) {
      console.error(`[${config.name}Health] Error loading data:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when expanded
  useEffect(() => {
    if (isExpanded && !cachedData && !isLoading) {
      loadData();
    }
  }, [isExpanded]);

  // Health status styling
  const getHealthStyle = (status: HealthStatus) => {
    switch (status) {
      case 'growing':
        return { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', icon: Icons.TrendingUp, label: t('health.growing', 'En croissance') };
      case 'stable':
        return { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', icon: Icons.Activity, label: t('health.stable', 'Stable') };
      case 'declining':
        return { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)', icon: Icons.TrendingDown, label: t('health.declining', 'En baisse') };
      default:
        return { color: 'var(--muted)', bg: 'var(--border)', icon: Icons.Loader2, label: t('health.loading', 'Chargement...') };
    }
  };

  const healthStyle = getHealthStyle(healthStatus);
  const HealthIcon = healthStyle.icon;

  // Format date
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="card" style={{ marginBottom: '20px', overflow: 'hidden' }}>
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          padding: '16px 20px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'var(--text)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            padding: '8px',
            background: config.gradientColors,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Icons.Activity size={20} style={{ color: 'white' }} />
          </div>
          <div style={{ textAlign: 'left' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>
              {t('health.title', 'Santé de la Blockchain')}
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
              {t('health.subtitle', 'Métriques réseau et activité développement')}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Health Status Badge */}
          <div style={{
            padding: '6px 12px',
            background: healthStyle.bg,
            color: healthStyle.color,
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <HealthIcon size={14} style={healthStatus === 'loading' ? { animation: 'spin 1s linear infinite' } : {}} />
            {healthStyle.label}
          </div>

          {/* Expand/Collapse Arrow */}
          <Icons.ChevronDown
            size={20}
            style={{
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{
          padding: '0 20px 20px 20px',
          borderTop: '1px solid var(--border)'
        }}>
          {isLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: 'var(--muted)'
            }}>
              <Icons.Loader2 size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '12px' }} />
              {t('health.loading', 'Chargement des métriques...')}
            </div>
          ) : (
            <>
              {/* Metrics Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px',
                marginTop: '16px'
              }}>
                {/* Network-specific Metrics */}
                {networkMetrics && config.renderNetworkMetrics && config.renderNetworkMetrics(networkMetrics)}

                {/* Supply Metrics from cached data */}
                {cachedData && (
                  <>
                    {/* Circulating Supply */}
                    <div style={{
                      padding: '16px',
                      background: 'var(--border)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.Coins size={24} style={{ color: config.primaryColor, marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: config.primaryColor }}>
                        {formatSupply(cachedData.circulating_supply)}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        {t('health.circulating', 'En Circulation')}
                      </div>
                    </div>

                    {/* Supply Percentage */}
                    <div style={{
                      padding: '16px',
                      background: 'var(--border)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.PieChart size={24} style={{ color: '#22c55e', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#22c55e' }}>
                        {cachedData.circulating_percent}%
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        {t('health.supplyPercent', 'du Max Supply')}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Token Emissions Section */}
              {nextEmissions.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '10px'
                  }}>
                    <Icons.Calendar size={16} style={{ color: 'var(--muted)' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)' }}>
                      {t('health.nextEmissions', 'Prochaines Émissions')}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {nextEmissions.map((emission, idx) => (
                      <div key={idx} style={{
                        padding: '10px 14px',
                        background: `${config.primaryColor}15`,
                        border: `1px solid ${config.primaryColor}30`,
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}>
                        <div style={{ fontWeight: 600, color: config.primaryColor }}>{emission.date}</div>
                        <div style={{ color: 'var(--muted)', marginTop: '2px' }}>{emission.amount}</div>
                        <div style={{ color: 'var(--muted)', fontSize: '10px' }}>{emission.type}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* GitHub Development Section */}
              {githubStats && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <Icons.Code size={16} style={{ color: 'var(--muted)' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)' }}>
                      {t('health.development', 'Activité Développement')} (GitHub)
                    </span>
                    <a
                      href={config.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        marginLeft: 'auto',
                        fontSize: '12px',
                        color: 'var(--accent)',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Icons.ExternalLink size={12} />
                      {config.githubRepoName}
                    </a>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                    gap: '10px'
                  }}>
                    {/* Stars */}
                    <div style={{
                      padding: '12px',
                      background: 'var(--border)',
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <Icons.Star size={18} style={{ color: '#fbbf24', marginBottom: '6px' }} />
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>{githubStats.stars}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Stars</div>
                    </div>

                    {/* Forks */}
                    <div style={{
                      padding: '12px',
                      background: 'var(--border)',
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <Icons.GitMerge size={18} style={{ color: '#a78bfa', marginBottom: '6px' }} />
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>{githubStats.forks}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Forks</div>
                    </div>

                    {/* Open Issues */}
                    <div style={{
                      padding: '12px',
                      background: 'var(--border)',
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <Icons.AlertCircle size={18} style={{ color: '#f97316', marginBottom: '6px' }} />
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>{githubStats.openIssues}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Issues</div>
                    </div>

                    {/* Recent Commits */}
                    <div style={{
                      padding: '12px',
                      background: 'var(--border)',
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <Icons.GitCompare size={18} style={{ color: '#22c55e', marginBottom: '6px' }} />
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>~{githubStats.recentCommits}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Commits/30j</div>
                    </div>

                    {/* Last Commit */}
                    <div style={{
                      padding: '12px',
                      background: 'var(--border)',
                      borderRadius: '10px',
                      textAlign: 'center',
                      gridColumn: 'span 2'
                    }}>
                      <Icons.Clock size={18} style={{ color: '#64748b', marginBottom: '6px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatDate(githubStats.lastCommitDate)}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
                        {t('health.lastCommit', 'Dernier commit')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Health Explanation */}
              <div style={{
                marginTop: '20px',
                padding: '14px',
                background: healthStyle.bg,
                borderRadius: '10px',
                borderLeft: `4px solid ${healthStyle.color}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <HealthIcon size={20} style={{ color: healthStyle.color }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: healthStyle.color }}>
                      {t('health.status', 'État')}: {healthStyle.label}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                      {healthStatus === 'growing' && t('health.growingDesc', 'Le réseau montre une activité de développement soutenue et une adoption croissante.')}
                      {healthStatus === 'stable' && t('health.stableDesc', 'Le réseau maintient une activité régulière avec un développement continu.')}
                      {healthStatus === 'declining' && t('health.decliningDesc', 'L\'activité du réseau montre des signes de ralentissement.')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Refresh Button */}
              <div style={{
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                {cachedData && (
                  <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                    {t('health.lastUpdate', 'Dernière mise à jour')}: {getTimeSinceUpdate(cachedData)}
                  </span>
                )}
                <button
                  onClick={() => loadData(true)}
                  disabled={isLoading}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: 'var(--border)',
                    color: 'var(--text)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: isLoading ? 0.5 : 1
                  }}
                >
                  <Icons.RefreshCw size={14} style={isLoading ? { animation: 'spin 1s linear infinite' } : {}} />
                  {t('health.refresh', 'Actualiser')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Network configurations for easy reuse
export const networkConfigs: Record<NetworkId, NetworkConfig> = {
  icp: {
    networkId: 'icp',
    name: 'ICP',
    githubUrl: 'https://github.com/dfinity/ic',
    githubRepoName: 'dfinity/ic',
    gradientColors: 'linear-gradient(135deg, #29ABE2, #522785)',
    primaryColor: '#29ABE2',
  },
  algorand: {
    networkId: 'algorand',
    name: 'Algorand',
    githubUrl: 'https://github.com/algorand/go-algorand',
    githubRepoName: 'algorand/go-algorand',
    gradientColors: 'linear-gradient(135deg, #000000, #333333)',
    primaryColor: '#6366f1',
  },
  cardano: {
    networkId: 'cardano',
    name: 'Cardano',
    githubUrl: 'https://github.com/IntersectMBO/cardano-node',
    githubRepoName: 'IntersectMBO/cardano-node',
    gradientColors: 'linear-gradient(135deg, #0033AD, #1A44B8)',
    primaryColor: '#0033AD',
  },
  xdc: {
    networkId: 'xdc',
    name: 'XDC',
    githubUrl: 'https://github.com/XinFinOrg/XDPoSChain',
    githubRepoName: 'XinFinOrg/XDPoSChain',
    gradientColors: 'linear-gradient(135deg, #1D6FA3, #2196F3)',
    primaryColor: '#1D6FA3',
  },
  vechain: {
    networkId: 'vechain',
    name: 'VeChain',
    githubUrl: 'https://github.com/vechain/thor',
    githubRepoName: 'vechain/thor',
    gradientColors: 'linear-gradient(135deg, #15BDFF, #7B68EE)',
    primaryColor: '#15BDFF',
  },
  axelar: {
    networkId: 'axelar',
    name: 'Axelar',
    githubUrl: 'https://github.com/axelarnetwork/axelar-core',
    githubRepoName: 'axelarnetwork/axelar-core',
    gradientColors: 'linear-gradient(135deg, #7B3FE4, #00D1FF)',
    primaryColor: '#00D1FF',
  },
  flux: {
    networkId: 'flux',
    name: 'Flux',
    githubUrl: 'https://github.com/RunOnFlux/flux',
    githubRepoName: 'RunOnFlux/flux',
    gradientColors: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
    primaryColor: '#06b6d4',
  },
  hedera: {
    networkId: 'hedera',
    name: 'Hedera',
    githubUrl: 'https://github.com/hashgraph/hedera-services',
    githubRepoName: 'hashgraph/hedera-services',
    gradientColors: 'linear-gradient(135deg, #8259EF, #222222)',
    primaryColor: '#8259EF',
  },
  cronos: {
    networkId: 'cronos',
    name: 'Cronos',
    githubUrl: 'https://github.com/crypto-org-chain/cronos',
    githubRepoName: 'crypto-org-chain/cronos',
    gradientColors: 'linear-gradient(135deg, #1199FA, #0D2E50)',
    primaryColor: '#1199FA',
  },
  thegraph: {
    networkId: 'thegraph',
    name: 'The Graph',
    githubUrl: 'https://github.com/graphprotocol/graph-node',
    githubRepoName: 'graphprotocol/graph-node',
    gradientColors: 'linear-gradient(135deg, #6747ED, #1E1E1E)',
    primaryColor: '#6747ED',
  },
};
